import React, { useState, useEffect } from 'react';
import { Gift, X, Sparkles, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const CreditReminderModal = ({ userEmail, isOpen, onClose, onBookNow }) => {
  const [creditData, setCreditData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userEmail) {
      fetchCredits();
    }
  }, [isOpen, userEmail]);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/credits/stats/${encodeURIComponent(userEmail)}`);
      setCreditData(response.data);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    onClose();
    // Scroll to booking section
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
    if (onBookNow) onBookNow();
  };

  if (!isOpen) return null;
  if (loading) return null;
  if (!creditData || creditData.credits_available <= 0) {
    onClose();
    return null;
  }

  const freeHours = Math.floor(creditData.credits_available / 3);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-black border border-purple-500/50 rounded-3xl p-8 max-w-md w-full shadow-2xl shadow-purple-500/20 animate-scaleIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X size={24} />
        </button>
        
        {/* Celebration Icon */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mb-4 animate-bounce">
            <Gift size={40} className="text-purple-900" />
          </div>
          
          {/* Stars */}
          <div className="flex justify-center space-x-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Welcome Back!
          </h2>
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-transparent bg-clip-text">
            <p className="text-4xl font-black mb-2">
              {freeHours} FREE HOUR{freeHours > 1 ? 'S' : ''}
            </p>
          </div>
          <p className="text-gray-300 text-lg">
            You've earned free studio time through our loyalty program!
          </p>
          <p className="text-purple-300 text-sm mt-2 flex items-center justify-center">
            <Sparkles size={14} className="mr-1" />
            Every 3 hours booked = 1 free hour
          </p>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-black/40 rounded-xl p-4 text-center border border-purple-500/20">
            <p className="text-3xl font-bold text-white">{creditData.total_hours || 0}</p>
            <p className="text-gray-400 text-sm">Hours Booked</p>
          </div>
          <div className="bg-black/40 rounded-xl p-4 text-center border border-purple-500/20">
            <p className="text-3xl font-bold text-green-400">${freeHours * 35}</p>
            <p className="text-gray-400 text-sm">Savings Value</p>
          </div>
        </div>
        
        {/* CTA Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleBookNow}
            className="w-full py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-purple-900 font-bold text-lg rounded-xl hover:from-yellow-300 hover:to-orange-400 transition-all transform hover:scale-105 flex items-center justify-center"
          >
            Book Now & Use Credits
            <ChevronRight size={24} className="ml-2" />
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 bg-transparent border border-purple-500/30 text-gray-300 font-semibold rounded-xl hover:bg-purple-500/10 transition-colors"
          >
            Maybe Later
          </button>
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          Credits never expire • Use anytime
        </p>
      </div>
    </div>
  );
};

export default CreditReminderModal;
